import { useEffect, useState } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import { HiDotsHorizontal } from 'react-icons/hi';
import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchAllManualVerificationSessions } from '../../../../redux/manualVerification/sessionsDetails/actions';
import {
  getAllManualVerificationSessions,
  getManualVerificationSessionLoadingStatus,
} from '../../../../redux/manualVerification/sessionsDetails/selectors';
import { ManualVerificationSession } from '../../../../redux/manualVerification/sessionsDetails/types';
import {
  setSelectedSessionId,
  updateActiveStep,
} from '../../../../redux/manualVerification/stepperDetails';
import { getManualVerificationSelectedAppId } from '../../../../redux/manualVerification/stepperDetails/selectors';
import { getAppAnalytics } from '../../../../redux/viewApp/appAnalytics/selectors';
import {
  statusTagColors,
  statusUpperCaseToLowerCase,
} from '../../../../utils/manual-verification-session/manual-verification-constants';
import { sortByCreatedAtDesc } from '../../../../utils/utils';

const availableSessionStatus: { label: string; value: string }[] = [
  { label: 'All Sessions', value: 'ALL' },
  { label: 'Verified Sessions', value: 'VERIFIED' },
  { label: 'Pending Sessions', value: 'PENDING' },
  { label: 'Rejected Sessions', value: 'REJECTED' },
];

export const ManualVerificationSessionsScreen = () => {
  const appAnalytics = useAppSelector(getAppAnalytics);
  const selectedAppId = useAppSelector(getManualVerificationSelectedAppId);
  const allManualVerificationSessions = useAppSelector(getAllManualVerificationSessions);
  const fetchingManualVerificationSessions = useAppSelector(
    getManualVerificationSessionLoadingStatus
  );

  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResponse, setFilteredResponse] = useState<ManualVerificationSession[]>(
    []
  );
  const [selectedSessionStatusFilter, setSelectedSessionStatusFilter] =
    useState<string>('ALL');

  useEffect(() => {
    dispatch(
      fetchAllManualVerificationSessions({
        appId: selectedAppId,
        queryType: 'ALL',
      })
    );
  }, []);

  useEffect(() => {
    if (
      searchQuery ||
      (selectedSessionStatusFilter?.length && selectedSessionStatusFilter !== 'ALL')
    ) {
      const filtered = filterSessions(
        allManualVerificationSessions,
        searchQuery,
        selectedSessionStatusFilter
      );
      setFilteredResponse(sortByCreatedAtDesc([...filtered]));
    } else {
      setFilteredResponse(sortByCreatedAtDesc([...allManualVerificationSessions]));
    }
  }, [searchQuery, allManualVerificationSessions, selectedSessionStatusFilter]);

  function filterSessions(
    sessions: ManualVerificationSession[],
    searchQuery: string,
    selectedSessionStatusFilter: string
  ) {
    return sessions.filter((session) => {
      const matchesSearchQuery = searchQuery?.length
        ? session.parameters.some((param) =>
            param.value?.toString().includes(searchQuery)
          ) ||
          session.sessionId.toString().includes(searchQuery) ||
          session.appId.toString().includes(searchQuery)
        : true;

      const matchesStatusFilter =
        selectedSessionStatusFilter !== 'ALL'
          ? session.status === selectedSessionStatusFilter
          : true;

      return matchesSearchQuery && matchesStatusFilter;
    });
  }

  function showNetworkRequestDump(sessionId: string) {
    dispatch(setSelectedSessionId(sessionId));
    dispatch(updateActiveStep(3));
  }

  function handleRefreshSessionsData() {
    dispatch(
      fetchAllManualVerificationSessions({
        appId: selectedAppId,
        queryType: 'ALL',
      })
    );
  }

  return (
    <>
      <Card backgroundColor="#F7F9FB">
        <CardBody>
          <Box width="100%" height="100%">
            <Flex mb={1} alignItems="center">
              <Heading className="regular-font" flex={1}>
                Manual Verification Requests for {appAnalytics?.appDetail?.name}
              </Heading>
            </Flex>
            <Flex alignItems="center" justify="space-between" my={2} mt={4}>
              <InputGroup w={500}>
                <InputRightElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputRightElement>
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  type="search"
                  placeholder="Search Sessions"
                  bg="white"
                />
              </InputGroup>
              <Flex alignItems="center" justifyContent="flex-end" gap={10}>
                <Select
                  size="md"
                  w={200}
                  colorScheme="blackAlpha"
                  color="black"
                  bgColor="white"
                  cursor="pointer"
                  onChange={(e) => setSelectedSessionStatusFilter(e.target.value)}
                  value={selectedSessionStatusFilter}
                  _hover={{ border: '1.1px solid black' }}
                >
                  {availableSessionStatus.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                <Tooltip label="Refresh" placement="right-start">
                  <IconButton
                    size="sm"
                    variant="outline"
                    aria-label="Refresh Session"
                    icon={<FiRefreshCcw />}
                    colorScheme="blackAlpha"
                    color="black"
                    bgColor="white"
                    _hover={{
                      transform: 'scale(1.05)',
                      border: '1.1px solid black',
                    }}
                    isLoading={fetchingManualVerificationSessions}
                    onClick={handleRefreshSessionsData}
                  />
                </Tooltip>
              </Flex>
            </Flex>

            <Flex direction="column" mt={5} gap={5}>
              {filteredResponse?.map((session) => (
                <Flex
                  key={session?.id}
                  _hover={{
                    border: '1px solid black',
                    borderRadius: 10,
                  }}
                  border="1px solid transparent"
                  cursor="pointer"
                  p={2}
                  transition="0.1s ease-in"
                >
                  <Flex
                    flex={1}
                    direction="column"
                    gap={1}
                    onClick={() => {
                      showNetworkRequestDump(session?.sessionId);
                    }}
                  >
                    <Heading className="medium-font">
                      Session Id -{session?.sessionId}
                    </Heading>
                    <Text className="small-font" color="rgba(0, 0, 0, 0.40)">
                      App Id - {session?.appId}
                    </Text>
                    <Text className="small-font" color="rgba(0, 0, 0, 0.40)">
                      Extracted data -{' '}
                      {session?.parameters
                        .map((param) => `${param.key} : ${param.value}`)
                        .join(', ')}
                    </Text>
                  </Flex>
                  <Flex
                    direction="column"
                    justifyContent="space-between"
                    alignItems="flex-end"
                  >
                    <Menu>
                      <MenuButton>
                        <HiDotsHorizontal />
                      </MenuButton>
                      <MenuList>
                        <MenuItem icon={<AddIcon />} command="⌘T">
                          New Tab
                        </MenuItem>
                      </MenuList>
                    </Menu>
                    <Tag
                      colorScheme={statusTagColors?.[session?.status]}
                      size="md"
                      borderRadius={20}
                      px={3}
                      py={1}
                      variant="subtle"
                      textTransform="capitalize"
                    >
                      {statusUpperCaseToLowerCase?.[session?.status] ?? '-'}
                    </Tag>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Box>
        </CardBody>
      </Card>
    </>
  );
};
